import { APP_DATE_FORMAT_EXPANDED, APP_LOCAL_DATE_FORMAT } from "@/config/constants";
import { IEdoChapter } from "@/shared/model/edo-chapter.model";
import { IEdoCourseMaterial } from "@/shared/model/edo-course-material.model";
import { IEdoFile } from "@/shared/model/edo-file.model";
import { getFileAvatar } from "@/shared/util/file-avatar-utils";
import {
  Badge,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemSecondaryAction,
  ListItemText,
} from "@/components/ui";
import { ChevronDown, ChevronUp, Download, Edit, Trash2 } from "lucide-react";
import axios from "axios";
import "moment/locale/it";
import React, { useEffect, useState } from "react";
import { TextFormat, Translate } from "react-jhipster";
import { ChapterRow } from "./components/ChapterRow";

interface MaterialRowProps {
  courseMaterial?: IEdoCourseMaterial;
  isLastElement?: boolean;
  showSubject?: boolean;
  extendedDate?: boolean;
  onOpenEditMaterial?: (material: IEdoCourseMaterial) => void;
  onOpenDeleteMaterial?: (material: IEdoCourseMaterial) => void;
  onOpenViewChapter: (chapter: IEdoChapter) => void;
  onOpenEditChapterUnitsDialog?: (entity: IEdoChapter) => void;
}

const MaterialRow = (props: MaterialRowProps) => {
  const [open, setOpen] = useState<boolean>(false);
  const [files, setFiles] = useState<IEdoFile[]>([]);
  const [loadingFiles, setLoadingFiles] = useState<boolean>(true);

  const {
    courseMaterial,
    isLastElement,
    showSubject,
    extendedDate,
    onOpenEditMaterial: handleOpenEditMaterial,
    onOpenDeleteMaterial: handleOpenDeleteMaterial,
    onOpenViewChapter: handleOpenViewChapter,
  } = props;

  const loading = !courseMaterial || loadingFiles;

  const handleOpen = () => {
    setOpen(!open);
  };

  const openFile = (file: IEdoFile) => {
    window.open(`/api/files/${file.id}/view`, "_blank");
  };

  const downloadFile = (file: IEdoFile) => {
    window.open(`/api/files/${file.id}/download`, "_blank");
  };

  useEffect(() => {
    const loadFiles = async () => {
      if (!courseMaterial?.id) return;
      setLoadingFiles(true);
      const response = await axios.get(`/course-materials/${courseMaterial.id}/files`);
      setFiles(response.data.data);
      setLoadingFiles(false);
    };

    if (courseMaterial && open) {
      loadFiles();
    }
  }, [courseMaterial, open]);

  const getContent = () => {
    if (loading) {
      return (
        <div className="flex justify-center py-6">
          <span className="h-5 w-5 animate-spin rounded-full border-2 border-primary border-t-transparent" />
        </div>
      );
    }

    if (!files.length && !courseMaterial?.chapter) {
      return (
        <div className="px-6 py-5 text-center">
          <p className="text-sm text-[var(--color-mockup-text-light)]">
            Il materiale non contiene file.
          </p>
        </div>
      );
    }

    return (
      <>
        {files.length > 0 && (
          <List className="border-t border-[var(--color-mockup-border)] pl-4 sm:pl-6">
            {files.map((file) => (
              <ListItem
                key={file.id}
                className="flex cursor-pointer items-center gap-3 px-2 py-2.5 transition-colors hover:bg-primary/5"
                onClick={() => openFile(file)}
              >
                <ListItemIcon className="shrink-0">
                  {getFileAvatar(file.fileType)}
                </ListItemIcon>
                <ListItemText
                  primary={
                    <span className="text-sm font-medium text-[var(--color-mockup-text)]">
                      {file.originalFileName}
                    </span>
                  }
                  secondary={
                    <span className="text-xs text-[var(--color-mockup-text-light)]">
                      <TextFormat
                        value={file.createdAt}
                        type="date"
                        format={APP_LOCAL_DATE_FORMAT}
                      />
                    </span>
                  }
                  className="min-w-0 flex-1"
                />
                <ListItemSecondaryAction
                  className="ml-2"
                  onClick={(e) => e.stopPropagation()}
                >
                  <IconButton
                    onClick={() => downloadFile(file)}
                    aria-label="Scarica file"
                    className="w-7 h-7 rounded-full text-[var(--color-mockup-text-light)] hover:text-primary hover:bg-primary/10"
                  >
                    <Download className="h-3.5 w-3.5" />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        )}
        {courseMaterial?.chapter && (
          <List className="border-t border-[var(--color-mockup-border)]">
            <ChapterRow
              nested
              chapter={courseMaterial.chapter}
              viewChapter={() => handleOpenViewChapter(courseMaterial.chapter)}
              editChapter={null}
              deleteChapter={null}
            />
          </List>
        )}
      </>
    );
  };

  if (!courseMaterial) return null;

  return (
    <>
      <ListItem
        className="flex cursor-pointer items-center gap-3 px-5 py-3.5 transition-colors hover:bg-primary/5"
        onClick={handleOpen}
      >
        <ListItemText
          primary={
            !showSubject ? (
              <span className="text-sm font-semibold text-[var(--color-mockup-text)]">
                {courseMaterial.title}
              </span>
            ) : (
              <span className="flex items-center gap-2">
                <span className="text-sm font-semibold text-[var(--color-mockup-text)]">
                  {courseMaterial.title}
                </span>
                <Badge variant="secondary" className="rounded-full text-[10px] font-bold px-2 py-0.5">
                  {courseMaterial?.course?.subject?.name}
                </Badge>
              </span>
            )
          }
          secondary={
            <span className="text-xs text-[var(--color-mockup-text-light)]">
              <TextFormat
                value={courseMaterial.createdAt}
                type="date"
                format={extendedDate ? APP_DATE_FORMAT_EXPANDED : APP_LOCAL_DATE_FORMAT}
              />
            </span>
          }
          className="min-w-0 flex-1"
        />
        <div className={`flex h-6 w-6 items-center justify-center rounded-full transition-colors ${open ? 'bg-primary/15 text-primary' : 'text-[var(--color-mockup-text-light)]'}`}>
          {open ? (
            <ChevronUp className="h-4 w-4" />
          ) : (
            <ChevronDown className="h-4 w-4" />
          )}
        </div>
      </ListItem>
      {open && (
        <div className="border-t border-[var(--color-mockup-border)] bg-[var(--color-surface-secondary)]/40">
          {getContent()}
          {(handleOpenEditMaterial != null || handleOpenDeleteMaterial != null) && (
            <div className="flex flex-wrap items-center justify-end gap-2 border-t border-[var(--color-mockup-border)] px-5 py-3">
              {handleOpenEditMaterial != null && (
                <button
                  className="flex items-center gap-1.5 rounded-full border border-[var(--color-mockup-border)] bg-surface px-3 py-1.5 text-xs font-bold text-[var(--color-mockup-text)] transition-colors hover:border-primary/30 hover:bg-primary/5"
                  onClick={() => handleOpenEditMaterial(courseMaterial)}
                >
                  <Edit className="h-3.5 w-3.5" />
                  <Translate contentKey="entity.action.edit" />
                </button>
              )}
              {handleOpenDeleteMaterial != null && (
                <button
                  className="flex items-center gap-1.5 rounded-full border border-[var(--color-mockup-coral)]/20 bg-[var(--color-mockup-coral-light)] px-3 py-1.5 text-xs font-bold text-[var(--color-mockup-coral)] transition-colors hover:bg-[var(--color-mockup-coral)]/15"
                  onClick={() => handleOpenDeleteMaterial(courseMaterial)}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                  Elimina
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default MaterialRow;
